/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MASQUERADER_LIST, GET_USER_LIST } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import toast from "react-hot-toast";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter: any = {};
    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria);
      } else {
        filter = this.convertCriteria(args?.criteria);
      }
    } else {
      return {
        isEmpty: true,
      };
    }
    const options = {
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
    };

    try {
      if (client) {
        const res = await client.query({
          query: GET_MASQUERADER_LIST,
          variables: {
            filter: filter,
            options: options,
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log(res.data, "res.data");
          let _rowData = this.formatUserTable(
            res?.data?.getUserAdvancedSearch?.content
          );
          console.log(_rowData, "rowData");
          return {
            ...config,
            ultUsers: _rowData,
            profileDetails: this.getFormattedProfile(
              res?.data?.getUserAdvancedSearch?.content
            ),
            isEmpty: res?.data?.getUserAdvancedSearch?.content?.length <= 0,
            totalCount: res?.data?.getUserAdvancedSearch?.count,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      result[key] = { contains: criteria[key] };
    }
    return result;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        transformedCriteria[inputData.id] = {
          [filterType.toLowerCase().replace("equal", "eq")]:
            filters[filterType],
        };
      }
    }
    return transformedCriteria;
  }
  private formatUserTable(data: any) {
    let _rowData: any = [];
    data.forEach((item: any) => {
      if (item.presentation.status === "active") {
        _rowData.push({
          id: item?.User?._id,
          name: item?.User?.fullName ?? "Null Null",
          email: item?.User?.email ?? "null",
          gender: item?.User?.gender ?? "-",
          invitedBy: item?.ReferalUsers[0]?.fullName ?? "-",
          section: item?.Section?.name ? item?.Section?.name : "_",
          sectionOption: item?.CostumeType?.name
            ? item?.CostumeType?.name
            : "_",
          paymentStatus: item?.paymentStats ? item?.paymentStats : "-",
          costumeID: item?.costumeID ? item?.costumeID : "-",
          boxStatus: item?.boxStatus ? item?.boxStatus : "-",
        });
      }
    });

    if (_rowData.length === 0) {
      toast.error("Sorry, No active presentations found for the user...");
    }

    return _rowData;
  }

  private getFormattedProfile(data: any) {
    let _profileInfo: any = {
      title: null,
      subtitle: null,
      image: data[0]?.image ? data[0]?.image : "",
      description: data[0]?.bio,
      listItems: this.updateSubTitles(config?.listItems, data[0]),
      buttons: [
        {
          key: data[0]?.User?._id,
          title: "View Payment",
          color: "secondary",
          isPresent: true,
        },
      ],
    };
    return _profileInfo;
  }

  private updateSubTitles(listItems: any, data: any) {
    return listItems
      .map((item: any) => {
        const key = item.key;
        const value = this.getItemValue(key, data);
        return { ...item, subTitle: value };
      })
      .filter(
        (item: any) =>
          item.subTitle !== null &&
          item.subTitle !== "" &&
          item.subTitle !== undefined
      );
  }
  private getItemValue(key: any, data: any) {
    if (data) {
      switch (key) {
        case "email":
          return data?.User?.email;
        case "fullName":
          return data?.User?.fullName;
        case "lastName":
          return data?.lastName;
        case "nationality":
          return data?.User?.country;
        case "image":
          return data?.image;
        case "phoneNumber":
          return data?.User?.primaryContactNo;
        case "dob":
          return data?.dob;
        case "gender":
          return data?.User?.gender;
        case "address1":
          return data?.User?.address?.addressLine1;
        case "address2":
          return data?.User?.address?.addressLine2;
        case "address3":
          return data?.User?.address?.addressLine3;
        case "city":
          return data?.User?.address?.city;
        case "state":
          return data?.User?.address?.state;
        case "country":
          return data?.User?.address?.country;
        case "zipcode":
          return data?.User?.address?.zipcode;
        case "costumeId":
          return data?.costumeID;
        case "costumeType":
          return data?.costumeType?.name;
        case "section":
          return data?.Section?.name;
        case "boxStatus":
          return data?.boxStatus;
        case "totalAmount":
          return data?.totalAmount;
        case "isVIP":
          return data?.isVIP ? "Yes" : "No";
        case "createdAt":
          return this.formatCreatedDate(data?.createdAt);
        default:
          return "";
      }
    }
    return "";
  }
  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }
}
